// Deterministic seeded RNG (AGENT.md §3). Named streams let genetics, escape
// checks and offline replay draw independently without disturbing each other.

/** Hashes an arbitrary string into a 32-bit seed. */
export function hashSeed(input: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export class Rng {
  private state: number;

  constructor(seed: number) {
    // 0 is a fixed point for the mixer, so nudge it off.
    this.state = (seed >>> 0) || 0x9e3779b9;
  }

  /** mulberry32 — small, fast, good enough distribution for gameplay RNG. */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Integer in [min, max]. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** True with probability p (p <= 0 never, p >= 1 always). */
  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T {
    return items[this.int(0, items.length - 1)];
  }

  /** Snapshot/restore so replays can resume mid-stream. */
  getState(): number {
    return this.state;
  }

  setState(state: number): void {
    this.state = state >>> 0;
  }
}

/**
 * Derives a named sub-stream from the world seed. Same world seed + same stream
 * name always yields the same sequence, independent of call order elsewhere.
 */
export function streamFor(worldSeed: number, name: string): Rng {
  return new Rng((worldSeed ^ hashSeed(name)) >>> 0);
}
